import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';

function WarehouseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [warehouse, setWarehouse] = useState(null);
  const [editedWarehouse, setEditedWarehouse] = useState(null);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchWarehouse();
  }, [id]);

  const fetchWarehouse = async () => {
    try {
      const res = await apiFetch(`/api/warehouses/${id}`);
      const data = await res.json();
      
      setWarehouse(data.warehouse);
      setEditedWarehouse(data.warehouse);
      setStock(data.stock || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching warehouse:', err);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/warehouses/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedWarehouse)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setWarehouse(updated);
        setEditedWarehouse(updated);
        alert('Warehouse updated successfully!');
      } else {
        alert('Failed to update warehouse');
      }
    } catch (err) {
      console.error('Error saving warehouse:', err);
      alert('Error saving warehouse');
    }
    setSaving(false);
  };

  // Group stock by category
  const groupedStock = stock.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  const categories = Object.keys(groupedStock).sort();

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleAllCategories = (expand) => {
    const newState = {};
    categories.forEach(cat => {
      newState[cat] = expand;
    });
    setExpandedCategories(newState);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading warehouse...</div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Warehouse not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/warehouses')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">{warehouse.name}</h1>
              <span className="text-sm text-blue-100">{warehouse.code}</span>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
            >
              {saving ? 'Saving...' : '💾 Save'}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('stock')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'stock'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Stock ({stock.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Warehouse Information</h2>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={editedWarehouse?.code || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse Name</label>
                  <input
                    type="text"
                    value={editedWarehouse?.name || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={editedWarehouse?.address || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={editedWarehouse?.city || ''}
                      onChange={(e) => setEditedWarehouse({...editedWarehouse, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input
                      type="text"
                      value={editedWarehouse?.postcode || ''}
                      onChange={(e) => setEditedWarehouse({...editedWarehouse, postcode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={editedWarehouse?.contact_name || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, contact_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="text"
                    value={editedWarehouse?.contact_phone || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, contact_phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={editedWarehouse?.contact_email || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, contact_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editedWarehouse?.is_default || false}
                      onChange={(e) => setEditedWarehouse({...editedWarehouse, is_default: e.target.checked})}
                      className="mr-2 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Default Warehouse</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editedWarehouse?.is_active || false}
                      onChange={(e) => setEditedWarehouse({...editedWarehouse, is_active: e.target.checked})}
                      className="mr-2 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editedWarehouse?.notes || ''}
                    onChange={(e) => setEditedWarehouse({...editedWarehouse, notes: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stock' && (
          <div>
            {stock.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="text-6xl mb-4">📦</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No stock in this warehouse</h3>
                <p className="text-gray-500">Stock will appear here when added</p>
              </div>
            ) : (
              <>
                {/* Expand/Collapse All */}
                <div className="mb-4 flex gap-2">
                  <button
                    onClick={() => toggleAllCategories(true)}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                  >
                    ▼ Expand All
                  </button>
                  <button
                    onClick={() => toggleAllCategories(false)}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                  >
                    ▶ Collapse All
                  </button>
                  <span className="text-sm text-gray-500 ml-2 self-center">
                    {categories.length} categories • {stock.length} products
                  </span>
                </div>

                {/* Grouped Stock */}
                <div className="space-y-4">
                  {categories.map(category => (
                    <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 flex items-center justify-between text-left transition"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-gray-600 font-mono text-sm">
                            {expandedCategories[category] ? '▼' : '▶'}
                          </span>
                          <h3 className="font-semibold text-gray-800">{category}</h3>
                          <span className="text-sm text-gray-500">
                            ({groupedStock[category].length} products)
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Total: {groupedStock[category].reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0).toFixed(0)}
                        </div>
                      </button>

                      {/* Category Items */}
                      {expandedCategories[category] && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Code</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reserved</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Available</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {groupedStock[category].map((item, index) => (
                                <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="font-mono text-sm text-blue-600">{item.sku}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{item.product_name}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">{item.location_code || '—'}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm font-semibold text-gray-900">{parseFloat(item.quantity || 0).toFixed(1)}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-orange-600">{parseFloat(item.reserved || 0).toFixed(1)}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`text-sm font-semibold ${
                                      parseFloat(item.available || 0) < 10 ? 'text-red-600' : 'text-green-600'
                                    }`}>
                                      {parseFloat(item.available || 0).toFixed(1)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default WarehouseDetail;
